import type { Movie, MovieCategory } from "@/types/movie";

export const categoryNameMap: Record<string, string> = {
  movie: "电影",
  tv: "电视剧",
  variety: "综艺",
};

export function getCategoryName(key: string): string {
  return categoryNameMap[key] || "影视";
}

export function isValidCategory(key: string): key is MovieCategory {
  return key === "movie" || key === "tv" || key === "variety";
}

export function formatPopularity(value: number): string {
  if (value >= 10000) {
    return `${(value / 10000).toFixed(1)}万`;
  }
  return String(value);
}

export function formatRating(rating: number): string {
  return rating.toFixed(1);
}

export type SortKey = "createdAt" | "popularity" | "rating";

export interface FilterParams {
  category?: MovieCategory;
  region?: string;
  year?: string;
  genre?: string;
  sort?: SortKey;
}

export function filterMovies(list: Movie[], params: FilterParams): Movie[] {
  const { category, region, year, genre } = params;
  return list.filter((m) => {
    if (category && m.category !== category) return false;
    if (region && region !== "全部地区" && m.region !== region) return false;
    if (year && year !== "全部年份" && String(m.year) !== year) return false;
    if (genre && genre !== "全部类型" && !m.genres.includes(genre)) return false;
    return true;
  });
}

export function sortMovies(list: Movie[], sort: SortKey = "createdAt"): Movie[] {
  const sorted = [...list];
  if (sort === "rating") {
    sorted.sort((a, b) => b.rating - a.rating);
  } else if (sort === "popularity") {
    sorted.sort((a, b) => b.popularity - a.popularity);
  } else {
    sorted.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  }
  return sorted;
}

export function searchMovies(list: Movie[], keyword: string): Movie[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return list.filter(
    (m) =>
      m.title.toLowerCase().includes(kw) ||
      m.summary.toLowerCase().includes(kw) ||
      m.director.toLowerCase().includes(kw) ||
      m.actors.some((a) => a.toLowerCase().includes(kw)) ||
      m.genres.some((g) => g.toLowerCase().includes(kw)),
  );
}

export function getRelatedMovies(list: Movie[], current: Movie, count = 6): Movie[] {
  const sameCategory = list.filter((m) => m.id !== current.id && m.category === current.category);
  const scored = sameCategory.map((m) => {
    let score = 0;
    if (m.genres.some((g) => current.genres.includes(g))) score += 3;
    if (m.director === current.director) score += 4;
    score += m.rating / 10;
    score += m.popularity / 1000000;
    return { m, score };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, count).map((s) => s.m);
}

export function paginate<T>(list: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
}